import React, { createContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import api from '../utils/api';

// Create the context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [messageApi, messageContextHolder] = message.useMessage();

  // Canonicalize backend role values to a stable set used by the frontend
  const normalizeRole = (roleRaw) => {
    const r = (roleRaw || '').toString().toLowerCase().trim();
    const map = {
      // client aliases
      'client': 'client',
      'customer': 'client',
      'client_user': 'client',
      'clientuser': 'client',
      'enduser': 'client',
      // agency aliases
      'agency': 'agency',
      'agency_admin': 'agency',
      // superadmin aliases
      'superadmin': 'superadmin',
      'super_admin': 'superadmin',
      'admin': 'superadmin',
      // whitelabel aliases
      'whitelabel_agency': 'whitelabel_agency',
      'white_label_agency': 'whitelabel_agency',
      'whitelabel': 'whitelabel_agency',
      'white-label': 'whitelabel_agency'
    };
    return map[r] || r;
  };

  // Check if user is authenticated on initial load
  useEffect(() => {
    const user = sessionStorage.getItem('user');
    const tokenLS = sessionStorage.getItem('token');
    if (user) {
      try {
        const parsed = JSON.parse(user);
        const normalized = parsed ? { ...parsed, role: normalizeRole(parsed.role) } : null;
        setCurrentUser(normalized);
        if (normalized) {
          sessionStorage.setItem('user', JSON.stringify(normalized));
        }
      } catch (error) {
        console.error('Failed to parse user data:', error);
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
      }
    } else if (tokenLS && !user) {
      // Orphaned token without user data; clear it to prevent redirect loops
      sessionStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  // Check if user is authenticated (use in-memory state to avoid race conditions)
  const isAuthenticated = useCallback(() => {
    return Boolean(token && currentUser);
  }, [token, currentUser]);

  // Check if user has specific role
  const hasRole = useCallback((role) => {
    return currentUser?.role === normalizeRole(role);
  }, [currentUser]);

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback((roles) => {
    if (!currentUser?.role) return false;
    const curr = normalizeRole(currentUser.role);
    const set = roles.map(r => normalizeRole(r));
    return set.includes(curr);
  }, [currentUser]);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      console.log('Attempting login with:', { email });
      const data = await api.login(email, password);
      
      if (!data || !data.token || !data.user) {
        throw new Error('Invalid response from server');
      }
      
      // Store user data and token temporarily
      const normalizedUser = { ...data.user, role: normalizeRole(data.user.role) };
      
      // Check if client is active (only for client users)
      if (normalizedUser.role === 'client') {
        try {
          console.log('Checking client status for user:', normalizedUser);
          const statusResponse = await fetch('/api/clients/me/status', {
            headers: { 
              'Authorization': `Bearer ${data.token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('Status check response:', statusResponse.status);
          
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            console.log('Client status data:', statusData);
            
            if (!statusData.is_active) {
              console.log('Client is inactive, blocking login');
              messageApi.error('Your account has been deactivated. Please contact your administrator.');
              throw new Error('Account deactivated');
            } else {
              console.log('Client is active, allowing login');
            }
          } else {
            const errorText = await statusResponse.text();
            console.warn('Status check failed:', statusResponse.status, errorText);
          }
        } catch (statusError) {
          console.error('Status check error:', statusError);
          if (statusError.message === 'Account deactivated') {
            throw statusError;
          }
          // If status check fails, allow login but log the error
          console.warn('Could not check client status:', statusError);
        }
      }
      
      // Store user data and token in sessionStorage (clears on browser close)
      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(normalizedUser));
      
      setToken(data.token);
      setCurrentUser(normalizedUser);
      
      console.log('Login successful for user:', normalizedUser);
      messageApi.success('Login successful!');
      return normalizedUser;
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      if (error.message === 'Account deactivated') {
        throw error;
      }
      
      const invalidCreds = error?.status === 400 || error?.status === 401;
      const errorMessage = invalidCreds
        ? 'Invalid email or password. Please try again.'
        : (error.message || 'Login failed. Please check your credentials.');
      
      messageApi.error(errorMessage);
      throw error;
    }
  }, [messageApi]);

  // Logout function
  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setToken(null);
    setCurrentUser(null);
    messageApi.success('Logged out successfully');
  }, [messageApi]);

  // Update user data
  const updateUser = useCallback((userData) => {
    setCurrentUser(prev => ({
      ...prev,
      ...userData
    }));
    sessionStorage.setItem('user', JSON.stringify({
      ...currentUser,
      ...userData
    }));
  }, [currentUser]);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      await api.post(`/users/${currentUser.id}/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      messageApi.success('Password changed successfully');
      return true;
    } catch (error) {
      console.error('Password change error:', error);
      messageApi.error(error.message || 'Failed to change password');
      throw error;
    }
  }, [currentUser, messageApi]);

  // Value to be provided by the context
  const value = {
    currentUser,
    token,
    loading,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    login,
    logout,
    updateUser,
    changePassword
  };

  return (
    <>
      {messageContextHolder}
      <AuthContext.Provider value={value}>
        {!loading && children}
      </AuthContext.Provider>
    </>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext, AuthProvider };

export default AuthContext;
